import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { StatCard } from "./shared";
import accountingNav, { ACCOUNTING_BASE } from "./accountingNav";

// Phase 10 (item 3 - "one Accounting source of truth"): this dashboard used
// to read /society/accounting/summary, /ledgers and /entries - the legacy
// Phase 1/2 society_accounting_entries collection, which the real Phase 3+
// double-entry engine (society_accounting_vouchers) does not write to since
// Billing switched to posting through it in Phase 6. That made every stat
// card here silently wrong for any Society actually using Accounting.
// Every figure below is now derived from the same engine-backed endpoints
// every report/voucher page already uses - Income & Expenditure (Phase 8),
// the engine's own Ledgers and Vouchers lists - never a separately
// maintained counter.
export default function AccountingDashboardPage() {
  const [ie, setIe] = useState({ totalIncome: 0, totalExpenditure: 0, surplus: 0, deficit: 0, isDeficit: false });
  const [ledgerCount, setLedgerCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [postedCount, setPostedCount] = useState(0);
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ieRes, ledgersRes, draftsRes, postedRes, setupRes] = await Promise.all([
          api.get("/society/accounting/reports/income-expenditure").catch(() => ({ data: null })),
          api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
          api.get("/society/accounting/engine/vouchers", { params: { status: "draft" } }).catch(() => ({ data: [] })),
          api.get("/society/accounting/engine/vouchers", { params: { status: "posted" } }).catch(() => ({ data: [] })),
          api.get("/society/accounting/setup").catch(() => ({ data: null })),
        ]);
        setIe(ieRes.data || { totalIncome: 0, totalExpenditure: 0, surplus: 0, deficit: 0, isDeficit: false });
        setLedgerCount(Array.isArray(ledgersRes.data) ? ledgersRes.data.length : 0);
        setDraftCount(Array.isArray(draftsRes.data) ? draftsRes.data.length : 0);
        setPostedCount(Array.isArray(postedRes.data) ? postedRes.data.length : 0);
        setSetup(setupRes.data || null);
      } catch (err) {
        toast.error(formatErr(err, "Unable to load accounting overview"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Accounting overview</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Landing page for the Society accounting module. Use the sections above to open ledgers, vouchers, banking, reports and audit tools.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Income (current FY)" value={`₹ ${Number(ie.totalIncome || 0).toLocaleString()}`} />
        <StatCard label="Expenditure (current FY)" value={`₹ ${Number(ie.totalExpenditure || 0).toLocaleString()}`} />
        <StatCard label={ie.isDeficit ? "Deficit (current FY)" : "Surplus (current FY)"} value={`₹ ${Number(ie.isDeficit ? ie.deficit : ie.surplus || 0).toLocaleString()}`} />
        <StatCard label="Ledgers" value={loading ? "-" : ledgerCount} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Draft vouchers</div>
          <div className="mt-1 text-2xl font-extrabold text-[#111827]">{loading ? "-" : draftCount}</div>
          <Link to={`${ACCOUNTING_BASE}/vouchers`} className="mt-2 inline-block text-xs font-semibold text-[#111827] underline">Review drafts</Link>
        </div>
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Go-live setup</div>
          <div className="mt-1 text-sm font-semibold text-[#111827]">
            {setup?.financialYear ? `FY ${setup.financialYear}` : "Not configured yet"}
          </div>
          <Link to={`${ACCOUNTING_BASE}/migration`} className="mt-2 inline-block text-xs font-semibold text-[#111827] underline">Open migration / go-live</Link>
        </div>
        <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Posted voucher count</div>
          <div className="mt-1 text-2xl font-extrabold text-[#111827]">{loading ? "-" : postedCount}</div>
          <Link to={`${ACCOUNTING_BASE}/reports/trial-balance`} className="mt-2 inline-block text-xs font-semibold text-[#111827] underline">View trial balance</Link>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-extrabold uppercase tracking-[0.16em] text-[#6B7280]">Quick access</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {accountingNav.filter((section) => section.type === "dropdown").map((section) => (
            <div key={section.group} className="rounded-sm border border-[#E5E7EB] bg-white p-4">
              <div className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#6B7280]">{section.group}</div>
              <div className="space-y-1.5">
                {section.items.map((item) => (
                  <Link key={item.to} to={item.to} className="block text-sm font-semibold text-[#111827] hover:underline">
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
